import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Card from "../components/Card";
import TabBar from "../components/TabBar";
import Button from "../components/Button";
import TextField from "../components/TextField";
import Table from "../components/Table";
import Tag from "../components/Tag";
import Pagination from "../components/Pagination";
import Dropdown from "../components/Dropdown";
import Modal from "../components/Modal";
import ChartSettings from "../components/ChartSettings";
import DownloadSettings from "../components/DownloadSettings";
import ShareSettings from "../components/ShareSettings";
import ShareEmailModal from "../components/ShareEmailModal";
import SpeciesSettings from "../components/SpeciesSettings.jsx";
import AuthorizationTypeSettings from "../components/AuthorizationTypeSettings";
import DateRangePicker from "../components/DateRangePicker";
import { loadFilterSection, saveFilterSection } from "../utils/filterStorage";
import {
    getCurrentSeasonDateRange,
    getCurrentSeasonStartYear,
    getSeasonDateRange,
    matchSeasonFromRange,
} from "../utils/seasonDateRange";
import { formatNumber } from "../utils/formatNumber";
import "./styles.css";

// Chart.js imports
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
);

// Importar imágenes de animales
import imgCorzo from "../assets/8b43135dbc0c9e16e1496fc50bb35c1676f2b40d.png";
import imgJabali from "../assets/35ee85eb15ac73963fb750c112a09261c36913b2.png";
import imgCabraMontes from "../assets/93300cd283de98250983c5cb1c9d7f0c35be5f26.png";
import imgCiervo from "../assets/8c8152eb2021b80f4880dee496903dade728006a.png";
import imgPalomaTorcaz from "../assets/d927dbbe0e85a2418fdc50ab89ee52120f52f9ce.png";
import imgMuflon from "../assets/d927dbbe0e85a2967834hdbaokcui37889ee52120f52f9ce.png";
import imgGamo from "../assets/d927dbbe0e85a2418fdc50ab89578120f52f9de.png";
import imgRebeco from "../assets/d927dbbe0e832k23b95n3lab89ee52120f52f9ce.png";
import imgTortola from "../assets/d927dbbe0e832349u8d95n3lab89ee52120f52f9ce.png";
import imgCodorniz from "../assets/img/especies/Codorniz.png";

// Importar iconos
import iconDownload from "../assets/icons/download.svg";
// import iconShare from '../assets/icons/share.svg'
import iconCog from "../assets/icons/settings.svg";
import iconTriangleUp from "../assets/icons/triangle-up.svg";
import iconTriangleDown from "../assets/icons/triangle-down.svg";

export default function Cupos() {
    const globalFilters = loadFilterSection("global") || {};
    const cuposFilters = loadFilterSection("cupos") || {};
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [permissions, setPermissions] = useState(null);
    
    // Datos iniciales del controlador
    const [especies, setEspecies] = useState([]);
    const [modalidades, setModalidades] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [cotos, setCotos] = useState([]);
    const [initialTotalCupos, setInitialTotalCupos] = useState({
        total: 0,
        usados: 0,
        disponibles: 0,
        porcentaje_usado: 0,
    });
    const [initialCuposPorEspecie, setInitialCuposPorEspecie] = useState([]);
    const [initialCuposPorEspecieSexo, setInitialCuposPorEspecieSexo] = useState([]);
    const [initialDistribucionCupos, setInitialDistribucionCupos] = useState({
        usados: 0,
        disponibles: 0,
        porcentaje_usados: 0,
        porcentaje_disponibles: 0,
    });
    const [initialCuposTable, setInitialCuposTable] = useState({
        data: [],
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
    });

    // Cargar datos iniciales
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [especiesRes, modalidadesRes, provinciasRes, cotosRes, permissionsRes] = await Promise.all([
                api.get('/especies'),
                api.get('/modalidades'),
                api.get('/provincias'),
                api.get('/cotos'),
                api.get('/auth/permissions')
            ]);

            // Transformar especies de {value, label} a {id, nombre}
            const especiesTransformadas = (especiesRes.data || []).map(especie => ({
                id: especie.value,
                nombre: especie.label
            }));

            setEspecies(especiesTransformadas);
            setModalidades(modalidadesRes.data || []);
            setProvincias((provinciasRes.data || [])
                .filter((p) => {
                    const label = typeof p === 'string' ? p : (p.label ?? p.nombre ?? '');
                    return !label.toLowerCase().includes('demo');
                })
                .map((p) => {
                    if (typeof p === 'string') return p.toUpperCase();
                    const label = p.label ?? p.nombre ?? '';
                    return { ...p, label: label.toUpperCase() };
                }));
            setCotos(cotosRes.data || []);
            // console.log('Permissions received:', permissionsRes.data);
            setPermissions(permissionsRes.data?.permissions ?? permissionsRes.data ?? null);
        } catch (err) {
            if (err.response?.status !== 401) {
                Sentry.captureException('Error loading initial data:', err);
            }
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Estados generales
    const [activeTab, setActiveTab] = useState("cupos");
    const [currentPage, setCurrentPage] = useState(
        initialCuposTable.current_page,
    );
    const [selectedProvincia, setSelectedProvincia] = useState(
        globalFilters.selectedProvincia || [],
    );
    const [selectedCoto, setSelectedCoto] = useState(
        globalFilters.selectedCoto || [],
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chartType, setChartType] = useState("barras");
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    //   const [isShareModalOpen, setIsShareModalOpen] = useState(false)
    const [isShareEmailModalOpen, setIsShareEmailModalOpen] = useState(false);
    const [isSpeciesModalOpen, setIsSpeciesModalOpen] = useState(false);
    const [fileType, setFileType] = useState("xls");
    const [shareType, setShareType] = useState("png");
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [isLoadingTable, setIsLoadingTable] = useState(false);

    // Estados para ordenamiento de la tabla
    const [sortBy, setSortBy] = useState("nombre_coto");
    const [sortOrder, setSortOrder] = useState("asc");

    // Estados para la tabla de precintos
    const [precintosTable, setPrecintosTable] = useState({
        data: [],
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
    });
    const [precintosSearchTerm, setPrecintosSearchTerm] = useState("");
    const [precintosCurrentPage, setPrecintosCurrentPage] = useState(1);
    const [precintosSortBy, setPrecintosSortBy] = useState("nr");
    const [precintosSortOrder, setPrecintosSortOrder] = useState("asc");
    const [isLoadingPrecintos, setIsLoadingPrecintos] = useState(false);

    const [isAuthorizationTypeModalOpen, setIsAuthorizationTypeModalOpen] =
        useState(false);

    // Estados de datos
    const [totalCupos, setTotalCupos] = useState(initialTotalCupos);
    const [cuposPorEspecie, setCuposPorEspecie] = useState(
        initialCuposPorEspecie,
    );
    const [cuposPorEspecieSexo, setCuposPorEspecieSexo] = useState(
        initialCuposPorEspecieSexo,
    );
    const [distribucionCupos, setDistribucionCupos] = useState(
        initialDistribucionCupos,
    );
    const [cuposTable, setCuposTable] = useState(initialCuposTable);

    // Date range por defecto = temporada actual completa (los chips de
    // "Temporada cinegética" del DateRangePicker permiten cambiar a otra).
    const [dateRange, setDateRange] = useState(() => {
        if (globalFilters.dateRange) return globalFilters.dateRange;
        return getSeasonDateRange(getCurrentSeasonStartYear());
    });

    // Si el rango coincide exactamente con una temporada, mostramos su nombre
    // en el título; si es un rango personalizado, devuelve null.
    const matchedSeason = useMemo(
        () => matchSeasonFromRange(dateRange.startDate, dateRange.endDate),
        [dateRange.startDate, dateRange.endDate],
    );

    // Lista de especies que se seleccionaban por defecto
    // const DEFAULT_SELECTED_SPECIES = [
    //     "Corzo",
    //     "Jabalí",
    //     "Cabra montés",
    //     "Ciervo",
    //     "Gamo",
    // ];

    // Inicializar especies visibles basándose en las especies por defecto
    // Usar initialCuposPorEspecie para obtener los IDs correctos ya que pueden diferir de especies
    const [visibleSpecies, setVisibleSpecies] = useState(
        () => globalFilters.visibleSpecies || cuposFilters.visibleSpecies || {},
    );

    useEffect(() => {
        saveFilterSection("global", {
            dateRange,
            selectedProvincia,
            selectedCoto,
            visibleSpecies,
        });
    }, [dateRange, selectedProvincia, selectedCoto, visibleSpecies]);

    const speciesSettingsRef = useRef(null);
    const chartSettingsRef = useRef(null);
    const shareEmailModalRef = useRef(null);
    const pageContentRef = useRef(null);

    // Función para obtener el icono correspondiente a cada animal
    const getAnimalIcon = (nombreEspecie) => {
        const iconMap = {
            Corzo: imgCorzo,
            Jabalí: imgJabali,
            "Cabra montés": imgCabraMontes,
            Ciervo: imgCiervo,
            "Paloma torcaz": imgPalomaTorcaz,
            "Muflón": imgMuflon,
            Rebeco: imgRebeco,
            Gamo: imgGamo,
            "Tórtola europea": imgTortola,
            Codorniz: imgCodorniz,
        };
        return iconMap[nombreEspecie] || imgCorzo; // Default icon
    };

    // Función para descargar en Excel
    const downloadExcel = async () => {
        try {
            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();

            // Hoja 1: Resumen General
            const wsResumen = workbook.addWorksheet("Resumen");
            wsResumen.addRow(["Panel de Datos - Cupos"]);
            wsResumen.addRow(["Fecha de generación:", new Date().toLocaleDateString("es-ES")]);
            wsResumen.addRow([
                "Rango de fechas:",
                `${formatDateForPicker(dateRange.startDate)} - ${formatDateForPicker(dateRange.endDate)}`,
            ]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Métricas Principales"]);
            wsResumen.addRow(["Total Cupos", totalCupos.total || 0]);
            wsResumen.addRow(["Cupos Usados", totalCupos.usados || 0]);
            wsResumen.addRow(["Cupos Disponibles", totalCupos.disponibles || 0]);
            wsResumen.addRow(["Porcentaje Usado (%)", totalCupos.porcentaje_usado || 0]);
            wsResumen.addRow([]);
            wsResumen.addRow(["Distribución de Cupos"]);
            wsResumen.addRow(["Usados", distribucionCupos.usados || 0]);
            wsResumen.addRow(["Disponibles", distribucionCupos.disponibles || 0]);
            wsResumen.addRow(["Porcentaje Usados (%)", distribucionCupos.porcentaje_usados || 0]);
            wsResumen.addRow([
                "Porcentaje Disponibles (%)",
                distribucionCupos.porcentaje_disponibles || 0,
            ]);

            // Hoja 2: Cupos por Especie
            if (cuposPorEspecie.length > 0) {
                const wsEspecie = workbook.addWorksheet("Cupos por Especie");
                wsEspecie.addRow([
                    "Especie",
                    "Total Precintos",
                    "Usados",
                    "Disponibles",
                ]);
                cuposPorEspecie
                    .filter((e) => visibleSpecies[e.especie_id])
                    .forEach((item) => {
                        wsEspecie.addRow([
                            item.nombre_especie,
                            item.total_precintos || 0,
                            item.usados || 0,
                            item.disponibles || 0,
                        ]);
                    });
            }

            // Hoja 3: Cupos por Especie y Sexo
            if (cuposPorEspecieSexo.length > 0) {
                // Agrupar por especie
                const especiesSexoAgrupadas = {};
                cuposPorEspecieSexo
                    .filter((item) => visibleSpecies[item.especie_id])
                    .forEach(item => {
                        if (!especiesSexoAgrupadas[item.especie_id]) {
                            especiesSexoAgrupadas[item.especie_id] = {
                                nombre_especie: item.nombre_especie,
                                machos: 0,
                                hembras: 0
                            };
                        }
                        if (item.sexo_code === 'm') {
                            especiesSexoAgrupadas[item.especie_id].machos = item.total_precintos || 0;
                        } else if (item.sexo_code === 'f') {
                            especiesSexoAgrupadas[item.especie_id].hembras = item.total_precintos || 0;
                        }
                    });

                const wsEspecieSexo = workbook.addWorksheet("Cupos por Especie y Sexo");
                wsEspecieSexo.addRow([
                    "Especie",
                    "Machos",
                    "Hembras",
                    "Total"
                ]);
                Object.values(especiesSexoAgrupadas).forEach((item) => {
                    wsEspecieSexo.addRow([
                        item.nombre_especie,
                        item.machos,
                        item.hembras,
                        item.machos + item.hembras
                    ]);
                });
            }

            // Hoja 4: Tabla de Cupos por Coto - Obtener todos los registros
            try {
                const params = new URLSearchParams({
                    search: searchTerm,
                    per_page: "999999", // Obtener todos los registros
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                });

                const response = await api.get("/cupos/data/table?" + params);
                const allData = response.data;

                if (true) {

                    if (allData.data && allData.data.length > 0) {
                        const wsTabla = workbook.addWorksheet("Cupos por Coto");
                        wsTabla.addRow([
                            "Nombre Coto",
                            "Matrícula",
                            "Provincia",
                            "Municipio",
                            "Especie",
                            "Cupo Utilizado",
                            "Porcentaje",
                        ]);
                        allData.data.forEach((item) => {
                            wsTabla.addRow([
                                item.nombre_coto,
                                item.matricula_coto || "",
                                item.provincia || "",
                                item.municipio || "",
                                item.especie,
                                item.cupo_utilizado,
                                item.porcentaje ? item.porcentaje + "%" : "",
                            ]);
                        });
                    }
                }
            } catch (error) {
                if (error.response?.status !== 401) {
                    Sentry.captureException("Error loading all data for export:", error);
                }
            }

            // Hoja 5: Tabla de Precintos - Obtener todos los registros
            try {
                const precintosParams = new URLSearchParams({
                    page: 1,
                    per_page: 10000,
                    search: precintosSearchTerm,
                    sort_by: precintosSortBy,
                    sort_order: precintosSortOrder,
                    start_date: dateRange.startDate,
                    end_date: dateRange.endDate,
                });

                if (selectedProvincia.length > 0) {
                    selectedProvincia.forEach(p => precintosParams.append('provincia[]', p));
                }
                if (selectedCoto.length > 0) {
                    selectedCoto.forEach(c => precintosParams.append('coto_id[]', c));
                }

                const precintosResponse = await api.get(`/cupos/data/precintos/table?${precintosParams.toString()}`);
                const precintosData = precintosResponse.data;

                if (precintosData.data && precintosData.data.length > 0) {
                    const wsPrecintos = workbook.addWorksheet("Todos los Precintos");
                    wsPrecintos.addRow([
                        'Número de Precinto',
                        'Nombre Coto',
                        'Matrícula',
                        'Provincia',
                        'Municipio',
                        'Especie',
                        'Sexo',
                        'Estado',
                        'Fecha de Creación'
                    ]);

                    precintosData.data.forEach((item) => {
                        wsPrecintos.addRow([
                            item.nr,
                            item.nombre_coto || '',
                            item.matricula_coto || '',
                            item.provincia || '',
                            item.municipio || '',
                            item.especie,
                            item.sexo === 'm' ? 'Macho' : item.sexo === 'f' ? 'Hembra' : 'No especificado',
                            item.estado,
                            item.fecha_creacion
                        ]);
                    });

                    // Ajustar anchos de columna
                    wsPrecintos.getColumn(1).width = 20;  // Número de Precinto
                    wsPrecintos.getColumn(2).width = 30;  // Nombre Coto
                    wsPrecintos.getColumn(3).width = 15;  // Matrícula
                    wsPrecintos.getColumn(4).width = 20;  // Provincia
                    wsPrecintos.getColumn(5).width = 20;  // Municipio
                    wsPrecintos.getColumn(6).width = 20;  // Especie
                    wsPrecintos.getColumn(7).width = 15;  // Sexo
                    wsPrecintos.getColumn(8).width = 15;  // Estado
                    wsPrecintos.getColumn(9).width = 18;  // Fecha de Creación
                }
            } catch (error) {
                if (error.response?.status !== 401) {
                    Sentry.captureException("Error loading precintos data for export:", error);
                }
            }

            const fileName = `cupos_${new Date().toISOString().split("T")[0]}.xlsx`;
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = fileName;
            link.click();
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException(new Error("Error exportando Excel: " + error.message));
            }
            alert("Error al generar el archivo Excel. Por favor, intente de nuevo.");
            console.error("Error en downloadExcel:", error);
        }
    };

    // Función para convertir imagen a base64
    const imageToBase64 = (img) => {
        return new Promise((resolve) => {
            if (!img.src || img.src.startsWith("data:")) {
                resolve(null);
                return;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const image = new Image();
            image.crossOrigin = "anonymous";

            image.onload = () => {
                canvas.width = image.naturalWidth || image.width;
                canvas.height = image.naturalHeight || image.height;
                ctx.drawImage(image, 0, 0);
                try {
                    const base64 = canvas.toDataURL("image/png");
                    resolve(base64);
                } catch (e) {
                    resolve(null);
                }
            };

            image.onerror = () => {
                resolve(null);
            };

            const separator = img.src.includes("?") ? "&" : "?";
            image.src = img.src + separator + "t=" + Date.now();
        });
    };

    // Función para descargar en PDF
    const downloadPDF = async () => {
        if (!pageContentRef.current) return;

        setIsDownloadModalOpen(false);
        await new Promise((resolve) => setTimeout(resolve, 300));

        try {
            // Imports dinámicos para mejor rendimiento
            const [html2canvasModule, jsPDFModule] = await Promise.all([
                import("html2canvas"),
                import("jspdf"),
            ]);
            const html2canvas = html2canvasModule.default;
            const jsPDF = jsPDFModule.default;

            const loadingDiv = document.createElement("div");
            loadingDiv.id = "pdf-loading";
            loadingDiv.style.cssText =
                "position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999;";
            loadingDiv.innerHTML =
                '<div style="background: white; padding: 20px 40px; border-radius: 8px; font-size: 16px;">Generando PDF...</div>';
            document.body.appendChild(loadingDiv);

            const images = pageContentRef.current.querySelectorAll("img");
            const originalSrcs = new Map();

            for (const img of images) {
                if (img.src && !img.src.startsWith("data:")) {
                    originalSrcs.set(img, img.src);
                    const base64 = await imageToBase64(img);
                    if (base64) {
                        img.src = base64;
                    }
                }
            }

            await new Promise((resolve) => setTimeout(resolve, 100));

            const canvas = await html2canvas(pageContentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: "#f5f5f5",
                imageTimeout: 0,
            });

            const imgData = canvas.toDataURL("image/png");
            const pdf = new jsPDF({
                orientation:
                    canvas.width > canvas.height ? "landscape" : "portrait",
                unit: "mm",
                format: "a4",
            });

            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
            const imgX = (pdfWidth - imgWidth * ratio) / 2;

            // * Quitamos el titulo por ahora *
            // pdf.setFontSize(16);
            // pdf.text("Panel de Datos - Cupos", pdfWidth / 2, 10, {
            //     align: "center",
            // });

            const scaledHeight = imgHeight * ratio;
            const pageContentHeight = pdfHeight - 20;

            if (scaledHeight <= pageContentHeight) {
                pdf.addImage(
                    imgData,
                    "PNG",
                    imgX,
                    15,
                    imgWidth * ratio,
                    imgHeight * ratio,
                );
            } else {
                let remainingHeight = imgHeight;
                let sourceY = 0;
                let isFirstPage = true;

                while (remainingHeight > 0) {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }

                    const pageHeight = pageContentHeight / ratio;
                    const sliceHeight = Math.min(pageHeight, remainingHeight);

                    const tempCanvas = document.createElement("canvas");
                    tempCanvas.width = imgWidth;
                    tempCanvas.height = sliceHeight;
                    const tempCtx = tempCanvas.getContext("2d");
                    tempCtx.drawImage(
                        canvas,
                        0,
                        sourceY,
                        imgWidth,
                        sliceHeight,
                        0,
                        0,
                        imgWidth,
                        sliceHeight,
                    );

                    const tempImgData = tempCanvas.toDataURL("image/png");
                    pdf.addImage(
                        tempImgData,
                        "PNG",
                        imgX,
                        isFirstPage ? 15 : 10,
                        imgWidth * ratio,
                        sliceHeight * ratio,
                    );

                    sourceY += sliceHeight;
                    remainingHeight -= sliceHeight;
                    isFirstPage = false;
                }
            }

            pdf.setFontSize(10);
            pdf.text(
                `Generado el ${new Date().toLocaleDateString("es-ES")} - Rango: ${formatDateForPicker(dateRange.startDate)} a ${formatDateForPicker(dateRange.endDate)}`,
                pdfWidth / 2,
                pdfHeight - 5,
                { align: "center" },
            );

            const fileName = `cupos_${new Date().toISOString().split("T")[0]}.pdf`;
            pdf.save(fileName);

            for (const [img, originalSrc] of originalSrcs) {
                img.src = originalSrc;
            }

            document.body.removeChild(loadingDiv);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException("Error generating PDF:", error);
            }
            const loadingDiv = document.getElementById("pdf-loading");
            if (loadingDiv) {
                document.body.removeChild(loadingDiv);
            }
            alert("Error al generar el PDF. Por favor, intente de nuevo.");
        }
    };

    // Función para manejar la descarga según el tipo seleccionado
    const handleDownload = () => {
        if (fileType === "xls") {
            downloadExcel();
            setIsDownloadModalOpen(false);
        } else if (fileType === "pdf") {
            downloadPDF();
        }
    };

    // Función para descargar solo los datos de la tabla de cupos en Excel
    const downloadCuposTableExcel = async () => {
        try {
            // Obtener TODOS los datos del API sin paginación
            const params = new URLSearchParams({
                search: searchTerm,
                per_page: "999999",
                sort_by: sortBy,
                sort_order: sortOrder,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/cupos/data/table?" + params);
            const allData = response.data.data || [];

            if (allData.length === 0) {
                alert("No hay datos para descargar");
                return;
            }

            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();
            
            // Crear hoja de trabajo
            const worksheet = workbook.addWorksheet("Cupos por Coto");
            
            // Agregar encabezados
            const headers = columns.map(col => col.label);
            const headerRow = worksheet.addRow(headers);
            
            // Estilo para encabezados
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2D6A4F' }
            };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
            
            // Agregar datos
            allData.forEach(row => {
                const rowData = columns.map(col => {
                    const value = row[col.key];
                    // Manejar renderizado especial
                    if (col.key === 'sexo') {
                        if (value === 'M') return 'Macho';
                        if (value === 'F') return 'Hembra';
                        if (value === 'I') return 'Indeterminado';
                        return value || '';
                    } else if (col.key === 'cupo_utilizado') {
                        const usado = row.cupo_utilizado || 0;
                        const total = row.cupo_total || 0;
                        const porcentaje = total > 0 ? ((usado / total) * 100).toFixed(0) : 0;
                        return `${usado}/${total} (${porcentaje}%)`;
                    }
                    return value || '';
                });
                worksheet.addRow(rowData);
            });
            
            // Ajustar ancho de columnas
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index].length;
                column.eachCell({ includeEmpty: false }, cell => {
                    const cellLength = cell.value ? cell.value.toString().length : 0;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = Math.min(maxLength + 2, 50);
            });
            
            // Generar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `cupos_tabla_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar Excel:", error);
            if (error.response?.status !== 401) {
                Sentry.captureException(error);
            }
            alert("Hubo un error al descargar el archivo Excel");
        }
    };

    // Función para descargar solo los datos de la tabla de precintos en Excel
    const downloadPrecintosTableExcel = async () => {
        try {
            // Obtener TODOS los datos del API sin paginación
            const params = new URLSearchParams({
                search: precintosSearchTerm,
                per_page: "999999",
                sort_by: precintosSortBy,
                sort_order: precintosSortOrder,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            if (selectedProvincia.length > 0) {
                selectedProvincia.forEach(p => params.append('provincia[]', p));
            }
            if (selectedCoto.length > 0) {
                selectedCoto.forEach(c => params.append('coto_id[]', c));
            }

            const response = await api.get(`/cupos/data/precintos/table?${params.toString()}`);
            const allData = response.data.data || [];

            if (allData.length === 0) {
                alert("No hay datos para descargar");
                return;
            }

            const ExcelJS = await import("exceljs");
            const workbook = new ExcelJS.default.Workbook();
            
            // Crear hoja de trabajo
            const worksheet = workbook.addWorksheet("Precintos");
            
            // Agregar encabezados
            const headers = precintosColumns.map(col => col.label);
            const headerRow = worksheet.addRow(headers);
            
            // Estilo para encabezados
            headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            headerRow.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF2D6A4F' }
            };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
            
            // Agregar datos
            allData.forEach(row => {
                const rowData = precintosColumns.map(col => {
                    const value = row[col.key];
                    // Manejar renderizado especial
                    if (col.key === 'sexo') {
                        if (value === 'M') return 'Macho';
                        if (value === 'F') return 'Hembra';
                        if (value === 'I') return 'Indeterminado';
                        return value || '';
                    } else if (col.key === 'estado') {
                        if (value === 'disponible') return 'Disponible';
                        if (value === 'asignado') return 'Asignado';
                        if (value === 'usado') return 'Usado';
                        return value || '';
                    }
                    return value || '';
                });
                worksheet.addRow(rowData);
            });
            
            // Ajustar ancho de columnas
            worksheet.columns.forEach((column, index) => {
                let maxLength = headers[index].length;
                column.eachCell({ includeEmpty: false }, cell => {
                    const cellLength = cell.value ? cell.value.toString().length : 0;
                    if (cellLength > maxLength) {
                        maxLength = cellLength;
                    }
                });
                column.width = Math.min(maxLength + 2, 50);
            });
            
            // Generar archivo
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `precintos_tabla_${new Date().toISOString().split('T')[0]}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al descargar Excel:", error);
            if (error.response?.status !== 401) {
                Sentry.captureException(error);
            }
            alert("Hubo un error al descargar el archivo Excel");
        }
    };

    // Efecto para inicializar especies visibles cuando se cargan las especies
    useEffect(() => {
        const source =
            cuposPorEspecie && cuposPorEspecie.length > 0
                ? cuposPorEspecie
                : especies;
        if (!source || source.length === 0) return;

        const getId = (especie) =>
            especie?.especie_id !== undefined ? especie.especie_id : especie?.id;

        if (Object.keys(visibleSpecies).length === 0) {
            const initial = {};
            source.forEach((especie) => {
                const id = getId(especie);
                if (id !== undefined) {
                    initial[id] = true;
                }
            });
            setVisibleSpecies(initial);
            return;
        }

        const missing = source.filter((especie) => {
            const id = getId(especie);
            return id !== undefined && !Object.prototype.hasOwnProperty.call(visibleSpecies, id);
        });
        if (missing.length > 0) {
            const next = { ...visibleSpecies };
            missing.forEach((especie) => {
                const id = getId(especie);
                next[id] = true;
            });
            setVisibleSpecies(next);
        }
    }, [especies, cuposPorEspecie, visibleSpecies]);

    // Efecto para cargar datos cuando cambian los filtros
    useEffect(() => {
        if (
            dateRange.startDate &&
            dateRange.endDate &&
            Object.keys(visibleSpecies).length > 0
        ) {
            loadData();
        }
    }, [dateRange.startDate, dateRange.endDate, visibleSpecies, selectedProvincia, selectedCoto]);

    const tabs = useMemo(() => {
        const allTabs = [
            { id: "general", label: "General" },
            { id: "usuarios", label: "Usuarios" },
            { id: "autorizaciones", label: "Autorizaciones" },
            { id: "cupos", label: "Cupos" },
            { id: "capturas", label: "Capturas" },
        ];
        // console.log('Calculating tabs with permissions:', permissions, 'canViewUsers:', permissions?.canViewUsers);
        
        // Temporalmente deshabilitado - mostrar siempre el tab de usuarios
        // Descomentar cuando el API devuelva correctamente canViewUsers
        
        if (!permissions?.canViewUsers) {
            // console.log('Filtering out usuarios tab');
            return allTabs.filter((tab) => tab.id !== "usuarios");
        }
        
        // console.log('Showing all tabs including usuarios');
        return allTabs;
    }, [permissions?.canViewUsers]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
        const routes = {
            general: "/general",
            usuarios: "/usuarios",
            autorizaciones: "/autorizaciones",
            cupos: "/cupos",
            capturas: "/capturas",
        };
        if (routes[tab]) navigate(routes[tab]);
    }, [navigate]);

    // Función para convertir fecha ISO a formato DD/MM/YYYY
    const formatDateForPicker = (dateString) => {
        if (!dateString) return "";
        const [year, month, day] = dateString.split("-");
        return `${day}/${month}/${year}`;
    };

    // Cargar datos de la tabla con paginación, búsqueda y ordenamiento
    const loadTableData = async (
        page = 1,
        search = "",
        sortColumn = sortBy,
        sortDir = sortOrder,
    ) => {
        setIsLoadingTable(true);
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                search: search,
                per_page: "10",
                sort_by: sortColumn,
                sort_order: sortDir,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            // Añadir filtros de provincia y coto
            if (Array.isArray(selectedProvincia) && selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) => params.append("provincia[]", p));
            }
            if (Array.isArray(selectedCoto) && selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            const response = await api.get("/cupos/data/table?" + params);
            const data = response.data;
            setCuposTable(data);
            setCurrentPage(data.current_page);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException("Error loading table data:", error);
            }
        } finally {
            setIsLoadingTable(false);
        }
    };

    // Cargar datos de gráficos
    const loadData = async () => {
        if (!dateRange.startDate || !dateRange.endDate) return;

        setIsLoadingData(true);
        try {
            const visibleSpeciesIds = Object.keys(visibleSpecies)
                .filter((id) => visibleSpecies[id])
                .join(",");
            const params = new URLSearchParams({
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
                species_ids: visibleSpeciesIds,
            });

            if (selectedProvincia.length > 0) {
                selectedProvincia.forEach((p) =>
                    params.append("provincia[]", p),
                );
            }
            if (selectedCoto.length > 0) {
                selectedCoto.forEach((c) => params.append("coto_id[]", c));
            }

            // Cargar datos de cupos totales
            const totalResponse = await api.get(`/cupos/data/total?${params}`);
            const totalData = totalResponse.data;
            setTotalCupos(totalData);

            // Cargar datos de cupos por especie
            const especieResponse = await api.get(
                `/cupos/data/por-especie?${params}`,
            );
            const especieData = especieResponse.data;
            setCuposPorEspecie(especieData);

            // Cargar datos de cupos por especie y sexo
            const especieSexoResponse = await api.get(
                `/cupos/data/por-especie-sexo?${params}`,
            );
            const especieSexoData = especieSexoResponse.data;
            setCuposPorEspecieSexo(especieSexoData);

            // Cargar distribución de cupos
            const distribucionResponse = await api.get(
                `/cupos/data/distribucion?${params}`,
            );
            const distribucionData = distribucionResponse.data;
            setDistribucionCupos(distribucionData);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException("Error loading data:", error);
            }
        } finally {
            setIsLoadingData(false);
        }
    };

    // Handler para cambio de página
    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    // Handler para ordenamiento
    const handleSort = useCallback(
        (columnKey) => {
            const newSortOrder =
                sortBy === columnKey && sortOrder === "asc" ? "desc" : "asc";
            setSortBy(columnKey);
            setSortOrder(newSortOrder);
            setCurrentPage(1);
        },
        [sortBy, sortOrder],
    );

    // Efecto consolidado para cargar datos de la tabla con debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (dateRange.startDate && dateRange.endDate) {
                const pageToLoad = searchTerm ? 1 : currentPage;
                loadTableData(pageToLoad, searchTerm, sortBy, sortOrder);
            }
        }, searchTerm ? 500 : 0);

        return () => clearTimeout(timeoutId);
    }, [currentPage, dateRange.startDate, dateRange.endDate, sortBy, sortOrder, selectedProvincia, selectedCoto, searchTerm]);

    // Cargar datos de la tabla de precintos
    const loadPrecintosTable = async (
        page = 1,
        search = "",
        sortColumn = precintosSortBy,
        sortDir = precintosSortOrder,
    ) => {
        setIsLoadingPrecintos(true);
        try {
            const params = new URLSearchParams({
                page: page,
                per_page: 10,
                search: search,
                sort_by: sortColumn,
                sort_order: sortDir,
                start_date: dateRange.startDate,
                end_date: dateRange.endDate,
            });

            if (selectedProvincia.length > 0) {
                selectedProvincia.forEach(p => params.append('provincia[]', p));
            }
            if (selectedCoto.length > 0) {
                selectedCoto.forEach(c => params.append('coto_id[]', c));
            }

            const response = await api.get(`/cupos/data/precintos/table?${params.toString()}`);
            const data = response.data;
            setPrecintosTable(data);
        } catch (error) {
            if (error.response?.status !== 401) {
                Sentry.captureException("Error loading precintos table:", error);
            }
        } finally {
            setIsLoadingPrecintos(false);
        }
    };

    // Handler para cambio de página en tabla de precintos
    const handlePrecintosPageChange = useCallback((page) => {
        setPrecintosCurrentPage(page);
        loadPrecintosTable(page, precintosSearchTerm, precintosSortBy, precintosSortOrder);
    }, [precintosSearchTerm, precintosSortBy, precintosSortOrder, dateRange]);

    // Handler para ordenamiento en tabla de precintos
    const handlePrecintosSort = useCallback(
        (columnKey) => {
            const newSortOrder = precintosSortBy === columnKey && precintosSortOrder === "asc" ? "desc" : "asc";
            setPrecintosSortBy(columnKey);
            setPrecintosSortOrder(newSortOrder);
            setPrecintosCurrentPage(1);
        },
        [precintosSortBy, precintosSortOrder],
    );

    // Efecto consolidado para cargar datos de la tabla de precintos con debounce
    useEffect(() => {
        const timer = setTimeout(() => {
            if (dateRange.startDate && dateRange.endDate) {
                const pageToLoad = precintosSearchTerm ? 1 : precintosCurrentPage;
                loadPrecintosTable(pageToLoad, precintosSearchTerm, precintosSortBy, precintosSortOrder);
            }
        }, precintosSearchTerm ? 500 : 0);
        return () => clearTimeout(timer);
    }, [precintosCurrentPage, dateRange.startDate, dateRange.endDate, precintosSortBy, precintosSortOrder, selectedProvincia, selectedCoto, precintosSearchTerm]);



    // Configuración del gráfico de cupo total (Doughnut)
    const cupoTotalChartData = useMemo(
        () => ({
            labels: ["Usados", "Disponibles"],
            datasets: [
                {
                    data: [
                        distribucionCupos.porcentaje_usados,
                        distribucionCupos.porcentaje_disponibles,
                    ],
                    backgroundColor: ["#2D6A4F", "#95D5B2"],
                    borderWidth: 0,
                },
            ],
        }),
        [
            distribucionCupos.porcentaje_usados,
            distribucionCupos.porcentaje_disponibles,
        ],
    );

    const pieChartOptions = useMemo(
        () => ({
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
                legend: {
                    display: true,
                    position: "bottom",
                },
                tooltip: {
                    callbacks: {
                        label: function (context) {
                            return `${context.label}: ${context.parsed}%`;
                        },
                    },
                },
            },
        }),
        [],
    );

    // Filtrar especies visibles para el gráfico
    const especiesFiltradas = useMemo(
        () =>
            cuposPorEspecie
                .filter((especie) => visibleSpecies[especie.especie_id])
                .sort((a, b) => a.nombre_especie.localeCompare(b.nombre_especie)),
        [cuposPorEspecie, visibleSpecies],
    );

    // Configuración del gráfico de cupos por especie
    const cuposPorEspecieChartData = useMemo(
        () => ({
            labels: especiesFiltradas.map((item) => item.nombre_especie),
            datasets: [
                {
                    label: "Usados",
                    data: especiesFiltradas.map((item) => item.usados),
                    backgroundColor: "#2D6A4F",
                    borderColor: "#2D6A4F",
                    borderWidth: 1,
                },
                {
                    label: "Disponibles",
                    data: especiesFiltradas.map((item) => item.disponibles),
                    backgroundColor: "#95D5B2",
                    borderColor: "#95D5B2",
                    borderWidth: 1,
                },
            ],
        }),
        [especiesFiltradas],
    );

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "bottom",
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                stacked: true,
            },
            x: {
                stacked: true,
            },
        },
    };

    // Filtrar y preparar datos para el gráfico de especies por sexo
    const especiesSexoFiltradas = useMemo(() => {
        return cuposPorEspecieSexo.filter((item) =>
            visibleSpecies[item.especie_id]
        );
    }, [cuposPorEspecieSexo, visibleSpecies]);

    // Configuración del gráfico de cupos por especie y sexo
    const cuposPorEspecieSexoChartData = useMemo(() => {
        // Agrupar por especie
        const especies = {};
        especiesSexoFiltradas.forEach(item => {
            if (!especies[item.especie_id]) {
                especies[item.especie_id] = { machos: 0, hembras: 0, nombre_especie: item.nombre_especie };
            }
            if (item.sexo_code === 'm') {
                especies[item.especie_id].machos = item.total_precintos;
            } else if (item.sexo_code === 'f') {
                especies[item.especie_id].hembras = item.total_precintos;
            }
        });

        // Ordenar alfabéticamente por nombre de especie
        const especiesArray = Object.values(especies).sort((a, b) => 
            a.nombre_especie.localeCompare(b.nombre_especie)
        );

        const labels = especiesArray.map(e => e.nombre_especie);
        const machosData = especiesArray.map(e => e.machos);
        const hembrasData = especiesArray.map(e => e.hembras);

        return {
            labels: labels,
            datasets: [
                {
                    label: "Machos",
                    data: machosData,
                    backgroundColor: "#2D6A4F",
                    borderColor: "#2D6A4F",
                    borderWidth: 1,
                },
                {
                    label: "Hembras",
                    data: hembrasData,
                    backgroundColor: "#95D5B2",
                    borderColor: "#95D5B2",
                    borderWidth: 1,
                },
            ],
        };
    }, [especiesSexoFiltradas]);

    const barChartOptionsEspecieSexo = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: "bottom",
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                stacked: false,
            },
            x: {
                stacked: false,
            },
        },
    };

    // Columnas de la tabla
    const columns = [
        {
            key: "nombre_coto",
            label: "Nombre Coto",
            sortable: true,
        },
        {
            key: "matricula_coto",
            label: "Matrícula",
            sortable: true,
        },
        {
            key: "provincia",
            label: "Provincia",
            sortable: true,
        },
        {
            key: "municipio",
            label: "Municipio",
            sortable: true,
        },
        {
            key: "especie",
            label: "Especie",
            sortable: true,
        },
        {
            key: "sexo",
            label: "Sexo",
            sortable: true,
            render: (value) => {
                return (
                    <span>
                        {value === "m"
                            ? "Macho"
                            : value === "f"
                            ? "Hembra"
                            : "No especificado"}
                    </span>
                );
            },
        },
        {
            key: "cupo_utilizado",
            label: "Cupo utilizado",
            sortable: true,
            render: (value, row) => {
                const porcentaje = row.porcentaje;
                const match = value.match(/^(.+?)\s*\((\d+%)\)$/);
                const textoPrincipal = match ? match[1] : value;
                const porcentajeTexto = match ? match[2] : "";
                const colorPorcentaje =
                    porcentaje == 100 ? "var(--green-500)" : "#E47C26";

                return (
                    <span>
                        <span
                            style={{
                                color: "var(--dk-gray-600)",
                                fontWeight: 400,
                            }}
                        >
                            {textoPrincipal}
                        </span>
                        {porcentajeTexto && (
                            <span
                                style={{
                                    color: colorPorcentaje,
                                    fontWeight: 600,
                                }}
                            >
                                {" "}
                                ({porcentajeTexto})
                            </span>
                        )}
                    </span>
                );
            },
        },
    ];

    // Columnas de la tabla de precintos
    const precintosColumns = [

        {
            key: "nombre_coto",
            label: "Nombre Coto",
            sortable: true,
        },
        {
            key: "matricula_coto",
            label: "Matrícula",
            sortable: true,
        },
        {
            key: "nr",
            label: "Número de Precinto",
            sortable: true,
        },
        {
            key: "provincia",
            label: "Provincia",
            sortable: true,
        },
        {
            key: "municipio",
            label: "Municipio",
            sortable: true,
        },
        {
            key: "especie",
            label: "Especie",
            sortable: true,
        },
        {
            key: "sexo",
            label: "Sexo",
            sortable: true,
            render: (value) => {
                return (
                    <span>
                        {value === "m"
                            ? "Macho"
                            : value === "f"
                            ? "Hembra"
                            : "No especificado"}
                    </span>
                );
            },
        },
        {
            key: "estado",
            label: "Estado",
            sortable: true,
            render: (value) => {
                const isUsado = value === "Cazado";
                const isDisponible = value === "Disponible";
                return (
                    <span
                        style={{
                            color: isUsado ? "#E47C26" : isDisponible ? "var(--green-600)" : "var(--dk-gray-600)",
                            fontWeight: 500,
                        }}
                    >
                        {value}
                    </span>
                );
            },
        },
        {
            key: "fecha_creacion",
            label: "Fecha de Creación",
            sortable: true,
        },
    ];

    return (
        <>
            {loading && <LoadingScreen />}
            {error && <div className="error-message">Error: {error}</div>}
            {!loading && !error && (
        <div className="page-cupos" ref={pageContentRef}>
            <h1 className="page-title">Panel de datos del Sistema de control telemático de capturas en los cotos de caza de Castilla y León</h1>

            <div className="page-tabs">
                <TabBar
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </div>

            <div className="page-content">
                <div className="page-filters">
                    <div style={{display: 'flex', gap: '35px', flexWrap: 'wrap'}}>
                        <div className="filter-group">
                            <label className="filter-label">
                                Seleccione un rango de fechas
                            </label>
                            <DateRangePicker
                                startDate={formatDateForPicker(dateRange.startDate)}
                                endDate={formatDateForPicker(dateRange.endDate)}
                                onChange={setDateRange}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Provincia
                            </label>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Coto
                            </label>
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                        </div>
                        <div className="filter-group">
                            <label className="filter-label">
                                Especies
                            </label>
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() =>
                                    setIsSpeciesModalOpen(true)
                                }
                            >
                                {/* <img src={iconCog} alt="" /> */}
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>

                    <div className="filter-actions">
                        <Button
                            variant="accent"
                            onClick={() => setIsDownloadModalOpen(true)}
                        >
                            <img
                                src={iconDownload}
                                alt=""
                                className="button-icon button-icon-white"
                            />
                            <span>Descargar</span>
                        </Button>
                        {/* <Button variant="primary" onClick={() => setIsShareModalOpen(true)}>
                <img src={iconShare} alt="" />
            </Button> */}
                    </div>
                </div>

                {/* KPIs principales */}
                <div className="panel-section-title">
                    {matchedSeason != null
                        ? `Resumen — Temporada ${matchedSeason}/${(matchedSeason + 1).toString().slice(-2)}`
                        : "Resumen"}
                </div>
                <div className="panel-kpi-row">
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Total cupos</span>
                        <span className="kpi-value">
                            {formatNumber(totalCupos.total)}
                        </span>
                        <span className="kpi-foot">Precintos asignados</span>
                    </div>
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Usados</span>
                        <span className="kpi-value">
                            {formatNumber(distribucionCupos.usados)}
                        </span>
                        <span className="kpi-foot">
                            {distribucionCupos.porcentaje_usados != null
                                ? distribucionCupos.porcentaje_usados + "% del total"
                                : ""}
                        </span>
                    </div>
                    <div className="panel-kpi-card">
                        <span className="kpi-label">Disponibles</span>
                        <span className="kpi-value">
                            {formatNumber(distribucionCupos.disponibles)}
                        </span>
                        <span className="kpi-foot">
                            {distribucionCupos.porcentaje_disponibles != null
                                ? distribucionCupos.porcentaje_disponibles + "% del total"
                                : ""}
                        </span>
                    </div>
                    <div className="panel-kpi-card">
                        <span className="kpi-label">% utilización</span>
                        <span className="kpi-value">
                            {distribucionCupos.porcentaje_usados != null
                                ? distribucionCupos.porcentaje_usados + "%"
                                : "0%"}
                        </span>
                        <span className="kpi-foot">Cupos usados / total</span>
                    </div>
                </div>

                {/* Especies registradas */}
                <div className="panel-section-title">Cupos por especie</div>
                <Card>
                    <div className="animals-grid">
                        {cuposPorEspecie
                            ?.filter((especie) => visibleSpecies[especie.especie_id])
                            .sort((a, b) =>
                                a.nombre_especie.localeCompare(b.nombre_especie),
                            )
                            .map((especie) => (
                                <div
                                    key={especie.especie_id}
                                    className="animal-item"
                                >
                                    <img
                                        src={getAnimalIcon(especie.nombre_especie)}
                                        alt={especie.nombre_especie}
                                        className="animal-icon"
                                    />
                                    <div className="animal-info">
                                        <p className="animal-name">
                                            {especie.nombre_especie}
                                        </p>
                                        <p className="animal-value">
                                            {formatNumber(especie.total_precintos)}
                                        </p>
                                    </div>
                                </div>
                            ))}
                    </div>
                </Card>

                {/* Análisis - gráficos */}
                <div className="panel-section-title">Análisis</div>
                <Card>
                    <div className="card-section">
                        <div className="card-section-header">
                            <p className="card-section-title">
                                Cupos por especie (usados / disponibles)
                            </p>
                            <div className="card-actions">
                                <Button
                                    variant="primary"
                                    size="s"
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    <img src={iconCog} alt="" />
                                </Button>
                            </div>
                        </div>
                        <div
                            className="chart-container-large card-section-chart"
                            style={{ height: "260px" }}
                        >
                            {isLoadingData ? (
                                <div style={{ color: "var(--dk-green-600)" }}>
                                    Cargando datos...
                                </div>
                            ) : especiesFiltradas.length > 0 ? (
                                chartType === "lineal" ? (
                                    <Line
                                        data={cuposPorEspecieChartData}
                                        options={barChartOptions}
                                    />
                                ) : (
                                    <Bar
                                        data={cuposPorEspecieChartData}
                                        options={{
                                            ...barChartOptions,
                                            indexAxis: "x",
                                        }}
                                    />
                                )
                            ) : (
                                <div style={{ color: "var(--neutral-500)" }}>
                                    No hay datos disponibles. Selecciona
                                    especies en ajustes.
                                </div>
                            )}
                        </div>
                    </div>
                </Card>

                <div className="panel-charts-grid">
                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Cupo total precintos
                                </p>
                            </div>
                            <div
                                className="card-pie-chart"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: "260px",
                                }}
                            >
                                <div style={{ width: "240px", height: "240px" }}>
                                    {isLoadingData ? (
                                        <div style={{ color: "var(--dk-green-600)" }}>
                                            Cargando...
                                        </div>
                                    ) : (
                                        <Doughnut
                                            data={cupoTotalChartData}
                                            options={pieChartOptions}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <div className="card-section">
                            <div className="card-section-header">
                                <p className="card-section-title">
                                    Cupos por especie y sexo
                                </p>
                            </div>
                            <div
                                className="chart-container-large card-section-chart"
                                style={{ height: "260px" }}
                            >
                                {isLoadingData ? (
                                    <div style={{ color: "var(--dk-green-600)" }}>
                                        Cargando datos...
                                    </div>
                                ) : especiesSexoFiltradas.length > 0 ? (
                                    <Bar
                                        data={cuposPorEspecieSexoChartData}
                                        options={barChartOptionsEspecieSexo}
                                    />
                                ) : (
                                    <div style={{ color: "var(--neutral-500)" }}>
                                        No hay datos disponibles. Selecciona
                                        especies en ajustes.
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="page-table-section">
                    <div className="table-section-header">
                        <h3 className="table-section-title">Cupos por coto</h3>
                    </div>
                    <div className="table-filters">
                        <TextField
                            placeholder="Buscar por Coto o especie"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            withIcon={true}
                            className="table-search"
                        />
                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                    setCurrentPage(1);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() => setIsSpeciesModalOpen(true)}
                            >
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                            <Button
                                variant="accent"
                                size="s"
                                onClick={downloadCuposTableExcel}
                                title="Descargar tabla en Excel"
                            >
                                <img
                                    src={iconDownload}
                                    alt=""
                                    className="button-icon button-icon-white"
                                />
                            </Button>
                        </div>
                        {/* <Button variant="primary" size="s">
                            Filtrar
                        </Button> */}
                    </div>
                    {isLoadingTable ? (
                        <div
                            style={{
                                padding: "40px",
                                textAlign: "center",
                                color: "var(--dk-green-600)",
                            }}
                        >
                            Cargando datos...
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            data={cuposTable.data || []}
                            onSort={handleSort}
                            sortedColumn={sortBy}
                            sortOrder={sortOrder}
                        />
                    )}
                    <div className="table-pagination">
                        <Pagination
                            currentPage={cuposTable.current_page || 1}
                            totalPages={cuposTable.last_page || 1}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>

                {/* Nueva tabla de precintos */}
                <div className="page-table-section">
                    <div className="table-section-header">
                        <h3 className="table-section-title">Todos los Precintos</h3>
                    </div>
                    <div className="table-filters">
                        <TextField
                            placeholder="Buscar por número, coto o especie"
                            value={precintosSearchTerm}
                            onChange={(e) => setPrecintosSearchTerm(e.target.value)}
                            withIcon={true}
                            className="table-search"
                        />
                        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                            <Dropdown
                                options={provincias}
                                value={selectedProvincia}
                                onChange={(value) => {
                                    setSelectedProvincia(value);
                                    setPrecintosCurrentPage(1);
                                }}
                                placeholder="Todas las provincias"
                                searchable={true}
                                multiple={true}
                            />
                            <Dropdown
                                options={cotos}
                                value={selectedCoto}
                                onChange={(value) => {
                                    setSelectedCoto(value);
                                    setPrecintosCurrentPage(1);
                                }}
                                placeholder="Todos los cotos"
                                searchable={true}
                                multiple={true}
                            />
                            <Button
                                variant="primary"
                                size="s"
                                style={{ position: 'relative' }}
                                onClick={() => setIsSpeciesModalOpen(true)}
                            >
                                Selecciona las especies
                                {Object.values(visibleSpecies).filter(Boolean).length > 0 && (
                                    <span style={{ position: 'absolute', top: '-8px', right: '-8px', backgroundColor: '#2D6A4F', color: 'white', borderRadius: '50%', width: '22px', height: '22px', fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', lineHeight: '1', pointerEvents: 'none' }}>
                                        {Object.values(visibleSpecies).filter(Boolean).length}
                                    </span>
                                )}
                            </Button>
                            <Button
                                variant="accent"
                                size="s"
                                onClick={downloadPrecintosTableExcel}
                                title="Descargar tabla en Excel"
                            >
                                <img
                                    src={iconDownload}
                                    alt=""
                                    className="button-icon button-icon-white"
                                />
                            </Button>
                        </div>
                        {/* <Button variant="primary" size="s">
                            Filtrar
                        </Button> */}
                    </div>
                    {isLoadingPrecintos ? (
                        <div
                            style={{
                                padding: "40px",
                                textAlign: "center",
                                color: "var(--dk-green-600)",
                            }}
                        >
                            Cargando datos...
                        </div>
                    ) : (
                        <Table
                            columns={precintosColumns}
                            data={precintosTable.data || []}
                            onSort={handlePrecintosSort}
                            sortedColumn={precintosSortBy}
                            sortOrder={precintosSortOrder}
                        />
                    )}
                    <div className="table-pagination">
                        <Pagination
                            currentPage={precintosTable.current_page || 1}
                            totalPages={precintosTable.last_page || 1}
                            onPageChange={handlePrecintosPageChange}
                        />
                    </div>
                </div>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onApply={() => {
                    if (chartSettingsRef.current) {
                        chartSettingsRef.current.apply();
                    }
                    setIsModalOpen(false);
                }}
            >
                <ChartSettings
                    ref={chartSettingsRef}
                    chartType={chartType}
                    onChartTypeChange={setChartType}
                />
            </Modal>

            <Modal
                isOpen={isDownloadModalOpen}
                onClose={() => setIsDownloadModalOpen(false)}
                onApply={handleDownload}
                title="Descargar"
            >
                <DownloadSettings
                    fileType={fileType}
                    onFileTypeChange={setFileType}
                />
            </Modal>

            <Modal
                isOpen={isSpeciesModalOpen}
                onClose={() => setIsSpeciesModalOpen(false)}
                onApply={() => {
                    if (speciesSettingsRef.current) {
                        speciesSettingsRef.current.apply();
                        setIsSpeciesModalOpen(false);
                    }
                }}
            >
                <SpeciesSettings
                    ref={speciesSettingsRef}
                    especies={cuposPorEspecie.map((e) => ({
                        id: e.especie_id,
                        nombre: e.nombre_especie,
                    }))}
                    visibleSpecies={visibleSpecies}
                    onSpeciesChange={setVisibleSpecies}
                />
            </Modal>

            {/* <Modal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        onApply={() => {
          setIsShareModalOpen(false)
          setIsShareEmailModalOpen(true)
        }}
        title="Compartir"
      >
        <ShareSettings
          shareType={shareType}
          onShareTypeChange={setShareType}
        />
      </Modal> */}

            <Modal
                isOpen={isShareEmailModalOpen}
                onClose={() => setIsShareEmailModalOpen(false)}
                onApply={() => {
                    if (shareEmailModalRef.current) {
                        shareEmailModalRef.current.send();
                    }
                }}
                title="Compartir"
                applyLabel="Enviar"
                cancelLabel="Cancelar"
            >
                <ShareEmailModal
                    ref={shareEmailModalRef}
                    onSend={(email) => {
                        setIsShareEmailModalOpen(false);
                        // TODO: Implementar envío de archivo por email según shareType
                    }}
                    onCancel={() => setIsShareEmailModalOpen(false)}
                />
            </Modal>
        </div>
            )}
        </>
    );
}


